/**
 * @file 画面の向き制御モジュール
 *
 * 動画の全画面再生時に自動的に横向きにし、それ以外は縦固定にするための制御。
 *
 * ## プラットフォームごとの仕組み
 *
 * ### iOS
 * iOS の画面回転は `App-level ∩ VC-level` の積集合で決定される。
 * - App-level: `AppDelegate.supportedInterfaceOrientationsFor:window:` の戻り値
 * - VC-level: `CAPBridgeViewController.supportedInterfaceOrientations` の戻り値
 *
 * 2つのプラグインを**併用**して両方の層を制御する:
 * 1. **OrientationLockPlugin（カスタム）**: App-level のみを動的に変更する。
 *    起動時は .portrait に固定しスプラッシュ中の横向きを防止、
 *    全画面時は .landscape に変更して横向きを許可する。
 * 2. **@capacitor/screen-orientation（標準）**: VC-level の変更 + requestGeometryUpdate
 *    による強制回転を行う。カスタムプラグインの**フォールバック**としても機能する。
 *
 * カスタムプラグインが未登録の場合でも、標準プラグインが VC-level の変更と
 * 強制回転を担保するため、回転機能自体は動作する
 * （ただしスプラッシュ中の横向き防止は効かない）。
 *
 * ### Android
 * `@capacitor/screen-orientation` の `lock()` が内部で `setRequestedOrientation()` を呼び、
 * マニフェストの `android:screenOrientation="portrait"` をランタイムで上書きできる。
 * App-level/VC-level の二層構造がないため、カスタムプラグインは不要。
 *
 * ## 全画面再生時の回転フロー
 *
 * ```
 * 全画面開始 → lockLandscape()（Web では no-op）→ ネイティブでは横向きにロック
 *
 * 全画面解除 → lockPortrait() → 縦向きに戻す
 * ```
 *
 * Web（`Capacitor.getPlatform() === 'web'`）では `lockLandscape` / `unlockOrientation` は
 * 早期 return し、ブラウザでは向きを強制しない。
 */
import { Capacitor, registerPlugin } from '@capacitor/core'
import { ScreenOrientation } from '@capacitor/screen-orientation'

/**
 * iOS 専用カスタムプラグイン。
 * AppDelegate の orientationLock（App-level）のみを変更する。
 * VC-level の変更と requestGeometryUpdate による強制回転は標準プラグインに一任。
 * カスタムプラグインが未登録の場合はエラーになるが、catch で処理し
 * 後続の標準プラグイン呼び出しにフォールバックする。
 * @see ios/App/App/OrientationLockPlugin.swift
 */
interface OrientationLockPlugin {
  setLock(options: { orientation: string }): Promise<void>
}

const noopOrientationLock: OrientationLockPlugin = {
  setLock: async () => {},
}

/** iOS ネイティブのみ登録。Web / Android では不要なネイティブブリッジ登録を避ける */
const isIosNative =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
const OrientationLock: OrientationLockPlugin = isIosNative
  ? registerPlugin<OrientationLockPlugin>('OrientationLock')
  : noopOrientationLock

/**
 * iOS ネイティブで OrientationLockPlugin が利用可能かどうかを返す。
 *
 * 旧ネイティブビルド（プラグイン未搭載）に新 JS がライブアップデートされた場合、
 * setLock() が "not implemented" エラーを投げる。
 * 初回呼び出し時にプローブし、結果をキャッシュする。
 *
 * iOS ネイティブ以外では常に true を返す（カスタムプラグイン不要のため）。
 */
let _iosPluginAvailable: boolean | null = null
export const isOrientationLockPluginAvailable = async (): Promise<boolean> => {
  if (!isIosNative) return true
  if (_iosPluginAvailable !== null) return _iosPluginAvailable
  try {
    await OrientationLock.setLock({ orientation: 'portrait' })
    _iosPluginAvailable = true
  } catch {
    _iosPluginAvailable = false
  }
  return _iosPluginAvailable
}

/**
 * 画面の向きを縦 (Portrait) に強制する
 *
 * 以下のタイミングで呼び出される:
 * - アプリ起動時（bootstrap.ts）: 端末が横向きでも縦表示を保証
 * - 全画面動画再生の解除時: 縦向きに戻す
 * - Escキー/戻るボタンによる全画面解除時
 * - iOS の「Done」ボタンによる全画面解除時
 * - プレイヤーコンポーネントのアンマウント時（安全策）
 */
export const lockPortrait = async () => {
  if (Capacitor.getPlatform() === 'web') return
  if (Capacitor.getPlatform() === 'ios') {
    // iOS App-level: カスタムプラグインで AppDelegate.orientationLock を .portrait に設定。
    // await で完了を待ってから標準プラグインを呼ぶことで、
    // App-level と VC-level の変更が確実に順序通り反映される。
    await OrientationLock.setLock({ orientation: 'portrait' }).catch(e => {
      console.warn('[ScreenOrientation] OrientationLock plugin:', e)
    })
  }
  // 標準プラグイン: VC-level の変更 + requestGeometryUpdate による強制回転
  // iOS ではカスタムプラグインとの併用、Android では単独で使用
  await ScreenOrientation.lock({ orientation: 'portrait' }).catch(e => {
    console.warn('[ScreenOrientation] ScreenOrientation plugin:', e)
  })
}

/**
 * 画面の向きを横 (Landscape) に強制する
 *
 * 全画面動画再生の開始時に呼び出され、自動的に横向きに回転させる。
 * これにより、ユーザーに横向き表示が可能であることを伝える。
 *
 * 横向きの方向（左/右）は iOS が端末の物理的な向きに基づいて自動選択する。
 * `.landscape`（左右両方）を指定し特定方向を強制しないことで、
 * 端末の傾きと逆方向に回転する不自然な挙動を防いでいる。
 *
 */
export const lockLandscape = async () => {
  if (Capacitor.getPlatform() === 'web') return
  if (Capacitor.getPlatform() === 'ios') {
    // iOS App-level: カスタムプラグインで AppDelegate.orientationLock を .landscape に設定。
    // await で完了を待ってから標準プラグインを呼ぶことで、
    // App-level が先に landscape に変わった状態で VC-level の変更が行われ、
    // 積集合が正しく landscape になることを保証する。
    await OrientationLock.setLock({ orientation: 'landscape' }).catch(e => {
      console.warn('[ScreenOrientation] OrientationLock plugin:', e)
    })
  }
  // 標準プラグイン: VC-level の変更 + requestGeometryUpdate による強制回転
  await ScreenOrientation.lock({ orientation: 'landscape' }).catch(e => {
    console.warn('[ScreenOrientation] ScreenOrientation plugin:', e)
  })
}

/**
 * 画面の向き制限を解除し、端末の向きに応じた自由回転を許可する
 *
 * lockLandscape() で横向きにした直後に呼び出し、
 * 初期表示は横向きだが以降はユーザーが自由に回転できるようにする。
 * （横向き固定ではなく、ユーザーが縦に戻すことも可能にする）
 */
export const unlockOrientation = async () => {
  if (Capacitor.getPlatform() === 'web') return
  if (Capacitor.getPlatform() === 'ios') {
    // iOS: カスタムプラグインで App-level と VC-level の両方を any に設定。
    // 標準プラグインの unlock() はプラグイン初期化時に保存した VC-level を復元するが、
    // 初期化時は lockPortrait() で [portrait] になっているため、
    // unlock() を呼ぶとカスタムプラグインが設定した [portrait, landscapeL, landscapeR] が
    // [portrait] に上書きされてしまう。そのため iOS では unlock() を呼ばない。
    await OrientationLock.setLock({ orientation: 'any' }).catch(e => {
      console.warn('[ScreenOrientation] OrientationLock plugin:', e)
    })
    return
  }
  // Android: 標準プラグインで自由回転を許可
  await ScreenOrientation.unlock().catch(e => {
    console.warn('[ScreenOrientation] ScreenOrientation plugin:', e)
  })
}
